import { connect } from 'react-redux';
import { get, isEqual, keyBy, pick } from 'lodash';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

import { loading, loadingSuccess } from '@actions/loading';
import { openGenericModal } from '@actions/modal';
import {
  showConfirmationMessage,
  showErrorMessage,
  showSuccessMessage,
} from '@actions/messageconfirmation';

import SpinnerIcon from '@assets/icons/new-loader.svg';

import { Button, Tooltip } from '@commons/utils/styledLibraryComponents';
import { formatNewItemsOfDropdownSelection } from '@commons/utils/format';
import { getTheme } from '@commons/utils/theme';
import { LoadingIcon } from '@commons/Metrics/components/Stats/styledComponents';

import centralService from '@services/central';
import recipeService from '@services/recipe';

import { getEnableRecipeModalParams } from '@admin/products/recipes/utils/modalConfigurations';
import { getIsInCentralKitchenOnlyPage } from '@admin/utils/centralKitchen';

import { formatCompositions } from '../RecipeComposition/utils/format';

import { Container } from './styledComponents';

/**
 * Assert composition of the recipe is valid
 *
 * @param {Array} compositions  - The components contained in the recipe
 *
 * @returns {boolean} Whether the form is valid
 */
export const areCompositionsValid = (compositions) =>
  !compositions.some(
    (composition) =>
      composition.entity.id === null || !composition.quantity || !composition.entity.unit,
  );

/**
 * Assert that the general form of that page can be saved (recipe)
 *
 * @param {Object} ingredient  - The recipe to save
 * @param {Array} composition  - The components contained in the recipe
 *
 * @returns {boolean} Whether the form is valid
 */
export const isFormValid = (recipe, compositions, clientHasMultipleBrands) => {
  if (
    !recipe.name ||
    !recipe.categoryId ||
    !recipe.value ||
    !recipe.unit ||
    !compositions.length ||
    (clientHasMultipleBrands && !recipe.brands)
  ) {
    return false;
  }

  return areCompositionsValid(compositions);
};

const _hasFormChanged = (
  recipe,
  initialRecipe,
  compositions,
  initialCompositions,
  productionStoreEntityMappings,
  initialProductionStoreEntityMappings,
  clientHasMultipleChannels,
) => {
  const recipeFieldsToPick = [
    'img',
    'name',
    'value',
    'unit',
    'recipePreparationSteps',
    'brands',
    'inventoryListTemplates',
    'hasStock',
    'batchProduction',
    'hasLoss',
    'hasTransfer',
    'categoryId',
  ];

  if (clientHasMultipleChannels) {
    recipeFieldsToPick.push('hasMultipleChannels');
  }

  const hasRecipeInfoChanged = !isEqual(
    pick(recipe, recipeFieldsToPick),
    pick(initialRecipe, recipeFieldsToPick),
  );

  const formattedCompositionById = compositions.reduce(
    (acc, { entity: { id, name }, quantity, channelIds }) => {
      acc[id] = { name, quantity, channelIds };
      return acc;
    },
    {},
  );

  const formattedInitialCompositionById = initialCompositions.reduce(
    (acc, { entity: { id, name }, quantity, channels }) => {
      const channelIds = channels.map(({ id }) => id);

      acc[id] = { name, quantity, channelIds };

      return acc;
    },
    {},
  );

  const hasCompositionsChanged = !isEqual(
    formattedCompositionById,
    formattedInitialCompositionById,
  );

  if (productionStoreEntityMappings == null || initialProductionStoreEntityMappings == null) {
    return hasRecipeInfoChanged || hasCompositionsChanged;
  }

  const hasProductionChanged = !isEqual(
    productionStoreEntityMappings.map(({ storeId }) => storeId),
    initialProductionStoreEntityMappings.map(({ storeId }) => storeId),
  );

  return hasRecipeInfoChanged || hasCompositionsChanged || hasProductionChanged;
};

const _buildCompositionComparable = (composition = []) =>
  composition.reduce((acc, { entity: { id }, quantity, channelIds = [] }) => {
    acc[id] = { quantity, channelIds: [...channelIds].sort() };

    return acc;
  }, {});

const _haveVariantsChanged = (recipeVariants, initialRecipeVariants, deletedVariantIds) => {
  if (deletedVariantIds.length > 0) {
    return true;
  }

  if (recipeVariants.length !== initialRecipeVariants.length) {
    return true;
  }

  if (recipeVariants.some(({ isNew }) => isNew)) {
    return true;
  }

  const initialVariantById = keyBy(initialRecipeVariants, 'id');

  return recipeVariants.some((variant) => {
    const initialVariant = initialVariantById[variant.id];

    if (!initialVariant) {
      return true;
    }

    const hasStoresChanged = !isEqual(
      [...(variant.storeIds ?? [])].sort(),
      [...(initialVariant.storeIds ?? [])].sort(),
    );

    const hasCompositionChanged = !isEqual(
      _buildCompositionComparable(variant.composition),
      _buildCompositionComparable(initialVariant.composition),
    );

    return variant.name !== initialVariant.name || hasStoresChanged || hasCompositionChanged;
  });
};

const areVariantsValid = (recipeVariants) =>
  recipeVariants.every((variant) => {
    if (!variant.name || !variant.name.trim()) {
      return false;
    }

    if (!variant.composition || !variant.composition.length) {
      return false;
    }

    return areCompositionsValid(variant.composition);
  });

/**
 * Maps the local variant state to the recipe save payload. New variants (`isNew`) omit their id
 * so the back creates them
 */
const formatVariantsPayload = (recipeVariants) =>
  recipeVariants.map((variant) => {
    const compositions = variant.composition.map(
      ({ entity: { id: entityId }, quantity, channelIds }) => {
        const composition = { entityId, quantity };

        if (channelIds && channelIds.length) {
          composition.channelIds = channelIds;
        }

        return composition;
      },
    );

    const basePayload = {
      name: variant.name,
      storeIds: variant.storeIds,
      compositions,
    };

    if (variant.isNew) {
      return basePayload;
    }

    return { id: variant.id, ...basePayload };
  });

export const createRecipe = async (
  clientId,
  recipe,
  compositions,
  recipeVariants,
  showMessage,
  history,
  dispatchers,
) => {
  dispatchers.savingInProgress();

  const {
    img,
    name,
    value,
    unit,
    hasMultipleChannels,
    hasStock,
    hasLoss,
    hasTransfer,
    batchProduction,
    recipePreparationSteps,
    brands,
    inventoryListTemplates,
    categoryId,
  } = recipe;

  try {
    const brandIds = brands.map(({ id }) => id);

    const payload = {
      img,
      name,
      unit,
      value,
      hasMultipleChannels,
      hasStock,
      hasLoss,
      hasTransfer,
      batchProduction,
      compositions,
      recipePreparationSteps,
      brandIds,
      inventoryListTemplates: formatNewItemsOfDropdownSelection(inventoryListTemplates),
      categoryId,
      variants: formatVariantsPayload(recipeVariants),
    };

    getIsInCentralKitchenOnlyPage()
      ? await centralService.createKitchenRecipe(clientId, payload)
      : await recipeService.create(clientId, payload);

    showMessage(i18next.t('ADMIN.RECIPES.CREATE_SUCCESS'));

    history.goBack();
  } catch (err) {
    // Check if error is due to duplicate recipe name
    if (
      err.response.status === 409 &&
      get(err, 'response.data.message', '').includes('already used in another recipe')
    ) {
      showMessage(i18next.t('ADMIN.RECIPES.DUPLICATE_NAME_ERROR'), 'error');
    } else {
      showMessage(i18next.t('ADMIN.RECIPES.CREATE_FAILURE'), 'error');
    }
  } finally {
    dispatchers.savingFinished();
  }
};

export const updateRecipe = async (
  recipe,
  compositions,
  productionStoreEntityMappings,
  recipeVariants,
  deletedVariantIds,
  showMessage,
  history,
  dispatchers,
) => {
  dispatchers.savingInProgress();

  const {
    id,
    img,
    recipePreparationSteps,
    name,
    value,
    unit,
    hasMultipleChannels,
    hasStock,
    hasLoss,
    hasTransfer,
    batchProduction,
    brands,
    inventoryListTemplates,
    categoryId,
  } = recipe;

  const storeIdsToBeMapped = productionStoreEntityMappings.map(({ storeId }) => storeId);

  try {
    const brandIds = brands.map(({ id }) => id);

    const payload = {
      id,
      img,
      name,
      unit,
      value,
      hasMultipleChannels,
      hasStock,
      hasLoss,
      hasTransfer,
      batchProduction,
      compositions,
      recipePreparationSteps,
      brandIds,
      inventoryListTemplates: formatNewItemsOfDropdownSelection(inventoryListTemplates),
      categoryId,
      variants: formatVariantsPayload(recipeVariants),
      deletedVariantIds,
    };

    getIsInCentralKitchenOnlyPage()
      ? await centralService.patchKitchenRecipeById({
          ...payload,
          storeIds: storeIdsToBeMapped,
        })
      : await recipeService.patchById(payload);

    showMessage(i18next.t('ADMIN.RECIPES.MODIFY_SUCCESS'));

    history.goBack();
  } catch (err) {
    // Check if error is due to duplicate recipe name
    if (
      err.response.status === 409 &&
      get(err, 'response.data.message', '').includes('already used in another recipe')
    ) {
      showMessage(i18next.t('ADMIN.RECIPES.DUPLICATE_NAME_ERROR'), 'error');
    } else {
      showMessage(i18next.t('ADMIN.RECIPES.MODIFY_FAILURE'), 'error');
    }
  } finally {
    dispatchers.savingFinished();
  }
};

/**
 * Handle the save of the entire form (recipe)
 *
 * @param {Props} props - The props linked to the component
 *
 * @returns {void}
 */
export const handleSave = async (props) => {
  const {
    user,
    compositions: propsCompositions,
    recipe,
    showMessage,
    history,
    savingInProgress,
    savingFinished,
    recipeComponentsForm,
    productionStoreEntityMappings,
    channels,
    recipeVariants,
    deletedVariantIds,
    selectedVariantId,
  } = props;

  // The shared form mirrors the selected tab. When a variant tab is active the form holds the
  // variant's composition, so the main recipe's composition must be read from `compositions`
  // state (kept in sync by the detail page) instead of the form.
  const { recipeComponents: formEntities } = recipeComponentsForm.getValues();

  const mainComposition =
    selectedVariantId == null
      ? formatCompositions(formEntities, propsCompositions, channels)
      : propsCompositions;

  const formattedCompositions = mainComposition.map(
    ({ entity: { id: entityId, category, name }, quantity, channelIds, allergens }) => ({
      entityId,
      quantity,
      channelIds,
      allergens,
      category,
      name,
    }),
  );

  if (recipe.id) {
    return updateRecipe(
      recipe,
      formattedCompositions,
      productionStoreEntityMappings,
      recipeVariants,
      deletedVariantIds,
      showMessage,
      history,
      {
        savingInProgress,
        savingFinished,
      },
    );
  }

  await createRecipe(
    get(user, 'clientId'),
    recipe,
    formattedCompositions,
    recipeVariants,
    showMessage,
    history,
    {
      savingInProgress,
      savingFinished,
    },
  );
};

const renderAuditHistoryTrigger = ({ auditButtonLabel, auditButtonLoading, onOpenAuditDrawer }) => {
  if (auditButtonLoading) {
    return <LoadingIcon alt={'component-loader'} height="16px" src={SpinnerIcon} width="16px" />;
  }

  if (!auditButtonLabel) {
    return null;
  }

  const theme = getTheme();

  return (
    <Button
      buttonCustomStyle={{
        paddingLeft: '0px',
        height: '40px',
        font: theme.fonts.textSmallHeight14,
      }}
      color={'inpulse-outline'}
      fontColor={theme.colors.blacks.ipBlack4}
      handleClick={onOpenAuditDrawer}
      icon={'/images/inpulse/history.svg'}
      iconOnLeft={false}
      label={auditButtonLabel}
      noBorder
    />
  );
};

export const RecipeDetailsBottomBar = (props) => {
  const {
    recipe,
    compositions,
    isReadOnly,
    isSaveDisabled,
    onRecipeChange,
    openGenericModal,
    pageLoading,
    pageLoaded,
    showSuccessMessage,
    showErrorMessage,
    initialCompositions,
    initialRecipe,
    initialProductionStoreEntityMappings,
    productionStoreEntityMappings,
    clientHasMultipleBrands,
    clientHasMultipleChannels,
    recipeVariants = [],
    initialRecipeVariants = [],
    deletedVariantIds = [],
    auditButtonLabel,
    auditButtonLoading,
    onOpenAuditDrawer,
  } = props;

  const auditHistoryTrigger = renderAuditHistoryTrigger({
    auditButtonLabel,
    auditButtonLoading,
    onOpenAuditDrawer,
  });

  const [formIsValid, setFormIsValid] = useState(false);

  useEffect(() => {
    const formIsValid =
      isFormValid(recipe, compositions, clientHasMultipleBrands) &&
      areVariantsValid(recipeVariants);

    const formHasChanged =
      _hasFormChanged(
        recipe,
        initialRecipe,
        compositions,
        initialCompositions,
        productionStoreEntityMappings,
        initialProductionStoreEntityMappings,
        clientHasMultipleChannels,
      ) || _haveVariantsChanged(recipeVariants, initialRecipeVariants, deletedVariantIds);

    setFormIsValid(!isSaveDisabled && formIsValid && formHasChanged);
  }, [
    recipe,
    compositions,
    isSaveDisabled,
    productionStoreEntityMappings,
    recipeVariants,
    initialRecipeVariants,
    deletedVariantIds,
  ]);

  const enableRecipe = async (recipeToEnable) => {
    const recipeToEnableId = recipeToEnable.id;

    pageLoading();

    try {
      await recipeService.enableRecipe(recipeToEnableId);
      onRecipeChange({ ...recipe, active: true });
      showSuccessMessage(i18next.t('ADMIN.RECIPES.ENABLE_SUCCESS'));
    } catch {
      showErrorMessage(i18next.t('ADMIN.RECIPES.ENABLE_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const isEnableButtonDisabled = compositions.some(({ entity: { active } }) => !active);

  if (recipe.active || !recipe.id) {
    return (
      <Container>
        {auditHistoryTrigger}
        <Button
          color={'inpulse-default'}
          handleClick={() => formIsValid && handleSave(props)}
          icon={'/images/inpulse/save-white-small.svg'}
          isDisabled={!formIsValid || isReadOnly}
          label={i18next.t('GENERAL.SAVE')}
        />
      </Container>
    );
  }

  if (isEnableButtonDisabled) {
    return (
      <Container>
        {auditHistoryTrigger}
        <Tooltip
          displayBigger={true}
          place={'left'}
          renderTooltipTrigger={() => (
            <Button
              color={'inpulse-default'}
              handleClick={() => {}}
              icon={'/images/inpulse/power-white-small.svg'}
              isDisabled={true}
              label={i18next.t('GENERAL.ACTIVATE')}
            />
          )}
          text={i18next.t('ADMIN.RECIPES.ENABLED_DISABLE_TOOLTIP')}
        />
      </Container>
    );
  }

  return (
    <Container>
      {auditHistoryTrigger}
      <Button
        color={'inpulse-default'}
        handleClick={() => {
          const params = getEnableRecipeModalParams(recipe, enableRecipe);
          openGenericModal(params);
        }}
        icon={'/images/inpulse/power-white-small.svg'}
        label={i18next.t('GENERAL.ACTIVATE')}
      />
    </Container>
  );
};

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
});

const mapDispatchToProps = (dispatch) => ({
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
  showMessage: (message, type) => {
    dispatch(showConfirmationMessage(message, type));
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  savingInProgress: () => {
    dispatch(loading());
  },
  savingFinished: () => {
    dispatch(loadingSuccess());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(RecipeDetailsBottomBar);
